/**
 * Graph API client for Microsoft Graph API communication
 */

export class GraphApiClient {
  /**
   * Validate if a delta token is a proper Microsoft Graph URL
   */
  static isValidDeltaToken(deltaToken: string): boolean {
    if (!deltaToken) return false;
    
    // Check if it's a proper Microsoft Graph URL
    const isValidGraphUrl = deltaToken.startsWith('https://graph.microsoft.com/');
    
    // Check if it contains delta in the path or deltatoken parameter
    const hasDeltaInPath = deltaToken.includes('/delta') || deltaToken.includes('deltatoken=');
    
    return isValidGraphUrl && hasDeltaInPath;
  }

  /**
   * Fetch contacts using delta query from Microsoft Graph
   */
  static async fetchContactsDelta(
    accessToken: string,
    sharedUpn: string,
    deltaToken: string = ''
  ): Promise<any> {
    const selectFields = [
      'id',
      'displayName',
      'givenName',
      'surname',
      'emailAddresses',
      'companyName',
      'jobTitle',
      'businessPhones',
      'mobilePhone',
      'homePhones',
      'lastModifiedDateTime'
    ].join(',');
    
    let url: string;
    
    if (deltaToken) {
      // Validate delta token before using it
      if (!this.isValidDeltaToken(deltaToken)) {
        console.warn('Invalid delta token detected, falling back to initial delta query:', deltaToken);
        console.log('Delta token validation failed - URL starts with graph.microsoft.com:', deltaToken.startsWith('https://graph.microsoft.com/'));
        console.log('Delta token validation failed - contains delta:', deltaToken.includes('/delta') || deltaToken.includes('deltatoken='));
        // Fall back to initial delta query
        url = `https://graph.microsoft.com/v1.0/users/${sharedUpn}/contacts/delta?$select=${encodeURIComponent(selectFields)}`;
      } else {
        // Delta token is valid, use it directly
        console.log('Using valid corporate delta token:', deltaToken.substring(0, 100) + '...');
        url = deltaToken;
      }
    } else {
      // Initial delta query
      url = `https://graph.microsoft.com/v1.0/users/${sharedUpn}/contacts/delta?$select=${encodeURIComponent(selectFields)}`;
    }

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'odata.maxpagesize=999', // Request maximum page size
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch contacts delta: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Fetch a page of contacts from Microsoft Graph
   */
  static async fetchContactsPage(
    accessToken: string,
    sharedUpn: string,
    skipToken: string = '',
    top: number = 999
  ): Promise<any> {
    const selectFields = [
      'id',
      'displayName',
      'givenName',
      'surname',
      'emailAddresses',
      'companyName',
      'jobTitle',
      'businessPhones',
      'mobilePhone',
      'homePhones',
      'lastModifiedDateTime'
    ].join(',');
    
    let url = `https://graph.microsoft.com/v1.0/users/${sharedUpn}/contacts?$select=${encodeURIComponent(selectFields)}&$top=${top}`;
    
    if (skipToken) {
      url += `&$skiptoken=${encodeURIComponent(skipToken)}`;
    }

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'odata.maxpagesize=999', // Request maximum page size
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch contacts: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Fetch personal contacts using delta query from Microsoft Graph
   */
  static async fetchPersonalContactsDelta(
    accessToken: string,
    userUpn: string,
    deltaToken: string = ''
  ): Promise<any> {
    const selectFields = [
      'id',
      'displayName',
      'givenName',
      'surname',
      'emailAddresses',
      'companyName',
      'jobTitle',
      'businessPhones',
      'mobilePhone',
      'homePhones',
      'lastModifiedDateTime'
    ].join(',');
    
    let url: string;
    
    if (deltaToken) {
      // Validate delta token before using it
      if (!this.isValidDeltaToken(deltaToken)) {
        console.warn('Invalid personal delta token detected, falling back to initial delta query:', deltaToken);
        console.log('Personal delta token validation failed - URL starts with graph.microsoft.com:', deltaToken.startsWith('https://graph.microsoft.com/'));
        console.log('Personal delta token validation failed - contains delta:', deltaToken.includes('/delta') || deltaToken.includes('deltatoken='));
        // Fall back to initial delta query
        url = `https://graph.microsoft.com/v1.0/me/contacts/delta?$select=${encodeURIComponent(selectFields)}`;
      } else {
        // Delta token is valid, use it directly
        console.log('Using valid personal delta token:', deltaToken.substring(0, 100) + '...');
        url = deltaToken;
      }
    } else {
      // Initial delta query for personal contacts
      url = `https://graph.microsoft.com/v1.0/me/contacts/delta?$select=${encodeURIComponent(selectFields)}`;
    }

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'odata.maxpagesize=999', // Request maximum page size
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch personal contacts delta: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Fetch a page of personal contacts from Microsoft Graph
   */
  static async fetchPersonalContactsPage(
    accessToken: string,
    userUpn: string,
    skipToken: string = '',
    top: number = 999
  ): Promise<any> {
    const selectFields = [
      'id',
      'displayName',
      'givenName',
      'surname',
      'emailAddresses',
      'companyName',
      'jobTitle',
      'businessPhones',
      'mobilePhone',
      'homePhones',
      'lastModifiedDateTime'
    ].join(',');
    
    let url = `https://graph.microsoft.com/v1.0/me/contacts?$select=${encodeURIComponent(selectFields)}&$top=${top}`;
    
    if (skipToken) {
      url += `&$skiptoken=${encodeURIComponent(skipToken)}`;
    }

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'odata.maxpagesize=999', // Request maximum page size
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch personal contacts: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Fetch internal users using delta query from Microsoft Graph
   */
  static async fetchInternalUsersDelta(
    accessToken: string,
    deltaToken: string = ''
  ): Promise<any> {
    const selectFields = [
      'id',
      'displayName',
      'givenName',
      'surname',
      'mail',
      'userPrincipalName',
      'jobTitle',
      'department',
      'userType',
      'businessPhones',
      'mobilePhone',
      'companyName',
      'officeLocation',
      'otherMails'
    ].join(',');
    
    let url: string;
    
    if (deltaToken) {
      // Delta token is already a complete URL, use it directly
      console.log('Using internal delta token:', deltaToken.substring(0, 100) + '...');
      url = deltaToken;
    } else {
      // Initial delta query for internal users - no filter supported on delta endpoint
      console.log('Using initial internal delta query');
      url = `https://graph.microsoft.com/v1.0/users/delta?$select=${encodeURIComponent(selectFields)}`;
    }

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'odata.maxpagesize=999', // Request maximum page size
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch internal users delta: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Fetch a page of internal users from Microsoft Graph
   */
  static async fetchInternalUsersPage(
    accessToken: string,
    skipToken: string = '',
    top: number = 999
  ): Promise<any> {
    const selectFields = [
      'id',
      'displayName',
      'givenName',
      'surname',
      'mail',
      'userPrincipalName',
      'jobTitle',
      'department',
      'userType',
      'businessPhones',
      'mobilePhone',
      'companyName',
      'officeLocation',
      'otherMails'
    ].join(',');
    
    let url = `https://graph.microsoft.com/v1.0/users?$select=${encodeURIComponent(selectFields)}&$filter=userType eq 'Member' and accountEnabled eq true&$top=${top}`;
    
    if (skipToken) {
      url += `&$skiptoken=${encodeURIComponent(skipToken)}`;
    }

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'odata.maxpagesize=999', // Request maximum page size
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch internal users: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Fetch contacts from a specific URL (for pagination or delta links)
   */
  static async fetchContactsPageFromUrl(accessToken: string, url: string): Promise<any> {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch contacts: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return await response.json();
  }
}
